// The quiet manager's check-in (scenario J3, AI_MANAGERS.md §8,
// PLAN-2026-11-01-the-quiet-manager-check-in §3): the site asks a manager it
// has not seen for a while whether they are still in, and this is where the
// notice opens.
//
// §8.3's seven rules are the page's: assume life happened and never ask why;
// *I'm in* is one tap and nothing else; the bot is offered as help; no record
// and no league-mates; never "inactive"; no deadline; the door stays open.
// The bot is leaving, so what will happen is said before it happens.

import { useState } from 'react';
import { valueOf } from '@fsp/domain';
import { answerCheckIn, useCheckIn } from '../data/checkIn';
import { useLeagueBase, useRouteLeagueId } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { useActiveLeague } from '../data/LeaguesProvider';
import { Panel } from '../kit/Panel';
import { Button, Stack, Text } from '../kit/ui';
import { Confirm } from './shared/Confirm';
import { FromLeague } from './shared/FromLeague';
import '../styles/screens/league-screens.css';

export function CheckInScreen() {
  const leagueId = useRouteLeagueId();
  const base = useLeagueBase();
  const real = useLeagueSource() === 'api';
  const league = useActiveLeague()?.league.name ?? 'your league';
  const snapshot = useCheckIn(real ? leagueId : null);
  const [done, setDone] = useState<{
    answer: 'in' | 'leave';
    team: string | null;
    heir: string | null;
  } | null>(null);
  const [stepping, setStepping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answer = async (to: 'in' | 'leave', team: string | null, heir: string | null) => {
    if (leagueId === null) return;
    setBusy(true);
    setError(null);
    const reply = await answerCheckIn(leagueId, to);
    setBusy(false);
    if (!reply.ok) return setError(reply.message);
    setDone({ answer: to, team, heir });
  };

  const body = (content: React.ReactNode) => (
    <div className="fsp-screen">
      <div className="fsp-page-scroll">
        <div className="fsp-checkin">
          <Panel title={`Still in for ${league}?`}>{content}</Panel>
        </div>
      </div>
    </div>
  );

  if (!real)
    return body(
      <Text tone="secondary">The practice league asks nobody whether they are still in.</Text>,
    );
  if (done?.answer === 'in')
    return body(
      <Stack gap={3}>
        <Text>Glad you’re here.</Text>
        <div>
          <Button variant="primary" to={base}>
            My team
          </Button>
        </div>
      </Stack>,
    );
  if (done?.answer === 'leave')
    return body(
      <Stack gap={3}>
        <Text>
          {done.heir === null
            ? `A bot plays ${done.team ?? 'the team'} now. The team keeps your name in its history, and if you come back, the commissioner can hand it back.`
            : `${done.heir} runs ${done.team ?? 'the team'} now. The team keeps your name in its history.`}
        </Text>
        <div>
          <Button to="/">Your leagues</Button>
        </div>
      </Stack>,
    );
  return (
    <FromLeague snapshot={snapshot}>
      {(view) => {
        const ask = view?.ask ?? valueOf(snapshot)?.ask ?? null;
        if (ask === null)
          return body(
            <Stack gap={3}>
              <Text>Nothing to answer here: you’re in.</Text>
              {/* OPENING THE SITE ANSWERED IT; stepping back stays where it
                  always is, under You on Managers. */}
              <Text size="sm" tone="secondary">
                If you’d rather step back, Managers has it under You.
              </Text>
              <div className="fsp-checkin-actions">
                <Button variant="primary" to={base}>
                  My team
                </Button>
                <Button variant="quiet" to={`${base}/managers`}>
                  Managers
                </Button>
              </div>
            </Stack>,
          );
        const heir = ask.heir;
        return body(
          <Stack gap={3}>
            <Text>
              {heir === null
                ? `No pressure either way. If you’re in, one tap says so. If you’d rather step back, a bot can take ${ask.teamName} so the league keeps moving. The team keeps your name in its history, and it’s yours again if you come back.`
                : `No pressure either way. If you’re in, one tap says so. If you’d rather step back, ${heir} can run ${ask.teamName} on their own. The team keeps your name in its history.`}
            </Text>
            <div className="fsp-checkin-actions">
              <Button
                variant="primary"
                onClick={() => void answer('in', ask.teamName, heir)}
                loading={busy}
              >
                I’m in
              </Button>
              {stepping ? null : (
                <Button variant="secondary" onClick={() => setStepping(true)}>
                  {heir === null ? 'Have a bot take it' : 'Step back'}
                </Button>
              )}
            </div>
            {stepping ? (
              // SAID BEFORE IT HAPPENS (CLAUDE.md): this is leaving.
              <Stack gap={2}>
                <Text size="sm">
                  {heir === null
                    ? `A bot plays ${ask.teamName} from now on and you leave the league. Your waiver claims are withdrawn and offers nobody has accepted are void. The team keeps your name in its history, and if you come back, the commissioner can hand it back.`
                    : `You leave the league and ${heir} runs ${ask.teamName} on their own. Your waiver claims are withdrawn and offers nobody has accepted are void. The team keeps your name in its history.`}
                </Text>
                <div className="fsp-checkin-actions">
                  <Confirm
                    label={heir === null ? 'Hand it to a bot' : 'Leave the league'}
                    confirmLabel={heir === null ? 'Leave, and hand it over?' : 'Leave the league?'}
                    onConfirm={() => void answer('leave', ask.teamName, heir)}
                    busy={busy}
                    tone="warn"
                  />
                  <Button variant="quiet" onClick={() => setStepping(false)}>
                    Keep it
                  </Button>
                </div>
              </Stack>
            ) : null}
            {error === null ? null : (
              <Text size="sm" tone="bad">
                {error}
              </Text>
            )}
          </Stack>,
        );
      }}
    </FromLeague>
  );
}
